<template>
  <naj-box :title="local.lawyerForm.title">
    <v-row>
      <v-col
        cols="12"
        lg="3"
      >
        <naj-input
          :type-id="formFieldType.checkbox"
          :label="local.lawyerForm.isTraineeLawyer"
          :value.sync="formModel.isTraineeLawyer"
          :disabled="viewMode"
          @change="resetData"
        ></naj-input>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="12"
        lg="3"
      >
        <naj-input
          :type-id="formFieldType.selectbox"
          :label="local.attorney.type"
          :value.sync="formModel.attorneyTypeId"
          :disabled="viewMode"
          :items="attorneyTypes"
          item-text="name"
          item-value="id"
          validates="required"
          @change="attorneyTypeChanged"
        ></naj-input>
      </v-col>

      <v-col
        v-if="formModel.attorneyTypeId"
        cols="12"
        lg="3"
      >
        <naj-input
          :type-id="formFieldType.number"
          :label="`${showExtraExternalAttorneyFields? local.attorney.approvalNumber : local.attorney.number}`"
          :value.sync="formModel.attorneyNumber"
          :validates="`required|long|max:14|isValid:${local.attorney.invalidNumber},${isNumberValid}|isVerified:${isVerified}`"
          :disabled="viewMode"
          @input="changeVerification(false)"
        ></naj-input>
      </v-col>
      <v-col
        v-if="!formModel.isTraineeLawyer"
        cols="12"
        lg="3"
        class="d-flex justify-content-flex-start align-items-center"
      >
        <naj-button
          v-if="!isVerified"
          square
          inputs-alignment
          :loading="isChecking"
          :disabled="!isCheckingValidityValid || viewMode"
          @click="validateAttorneyNumber"
        >
          {{ local.common.check }}
        </naj-button>

        <naj-button
          v-else
          square
          inputs-alignment
          class="view-attorney-text"
          @click="showViewAttorneyModal"
        >
          {{ isInternalAttorney? local.attorney.attorneyText: local.attorney.attorneyDoc }}
        </naj-button>
      </v-col>
    </v-row>

    <v-row v-if="formModel.isTraineeLawyer">
      <v-col
        cols="12"
        lg="3"
      >
        <naj-input
          :type-id="formFieldType.number"
          :label="local.lawyerForm.mainLawyerSocialId"
          :value.sync="formModel.mainLawyerSocialId"
          :validates="`required|not_equal:${lawyerSocialId},${local.lawyerForm.mainAndTraineeLawyerEqualSocialIdsError}|isVerified:${isVerified}`"
          :disabled="viewMode"
          @input="changeVerification(false)"
        ></naj-input>
      </v-col>
      <v-col
        cols="12"
        lg="3"
      >
        <naj-input
          :type-id="formFieldType.number"
          :label="`${isExternalAttorney ? local.lawyerForm.mainAttorneyApprovalNumber : local.lawyerForm.mainAttorneyNumber}`"
          :value.sync="formModel.mainAttorneyNumber"
          :validates="`required|isVerified:${isVerified}`"
          :disabled="viewMode"
          @input="changeVerification(false)"
        ></naj-input>
      </v-col>
      <v-col
        cols="12"
        lg="3"
        class="d-flex justify-content-flex-start align-items-center"
      >
        <naj-button
          v-if="!isVerified"
          square
          inputs-alignment
          :disabled="!isCheckingValidityValid || viewMode"
          @click="validateAttorneyNumber"
        >
          {{ local.common.check }}
        </naj-button>
        <naj-button
          v-else
          square
          inputs-alignment
          class="view-attorney-text"
          @click="showViewAttorneyModal"
        >
          {{ local.attorney.attorneyText }}
        </naj-button>
      </v-col>
    </v-row>

    <v-row v-if="showExtraExternalAttorneyFields">
      <v-col
        cols="12"
        lg="3"
      >
        <naj-input
          :type-id="formFieldType.selectbox"
          :label="local.attorney.source"
          :value.sync="formModel.externalAttorneySourceId"
          :disabled="externalAttorneySourceDisabled"
          :items="countries"
          item-text="name"
          item-value="id"
          validates="required"
        ></naj-input>
      </v-col>

      <v-col
        cols="12"
        lg="2"
        class="align-self-center"
      >
        <naj-input
          :type-id="formFieldType.checkbox"
          :label="local.attorney.isApprovedFromMofa"
          :value.sync="formModel.isApprovedFromMofa"
          disabled
        ></naj-input>
      </v-col>
      <v-col
        cols="12"
        lg="2"
        class="align-self-center"
      >
        <naj-input
          :type-id="formFieldType.checkbox"
          :label="local.attorney.isApprovedFromMoj"
          :value.sync="formModel.isApprovedFromMoj"
          disabled
        ></naj-input>
      </v-col>
    </v-row>
    <attorney-modal
      v-if="attorneyData.attorneyCode"
      ref="viewAttorneyModal"
      :attorney-type="formModel.isTraineeLawyer? writingsOfJusticeEnum.internalAttorney : formModel.attorneyTypeId"
      :attorney-code="attorneyData.attorneyCode"
      :agent-social-id="attorneyData.agentSocialId"
      :client-social-id="attorneyData.clientSocialId"
      :attorney-details="attorneyDetails"
      :unified-number="formModel.unifiedNumber"
      @onError="changeVerification(false)"
    ></attorney-modal>
  </naj-box>
</template>

<script>
import AttorneyModal from '@/modules/lawsuit/components/attorney-modal';
import { writingsOfJusticeEnum, partyTypeEnum } from '@/helpers/enum';
import { createNamespacedHelpers } from 'vuex';

const { mapState: mapLUTState, mapGetters: mapLUTGetters } = createNamespacedHelpers('LookupsModule');
const { mapActions: mapSysActions } = createNamespacedHelpers('SystemModule');
const { mapGetters } = createNamespacedHelpers('LawsuitModule');

export default {
  inject: ['$validator'],
  components: { AttorneyModal },
  props: ['formModel', 'lawyerSocialId', 'viewMode'],
  data() {
    return {
      isChecking: false,
      externalAttorneySourceDisabled: true,
      attorneyDetails: {},
      writingsOfJusticeEnum,
    };
  },
  computed: {
    ...mapLUTState(['attorneyTypes', 'countries']),
    ...mapLUTGetters(['countriesForNonSaudi']),
    showExtraExternalAttorneyFields() {
      return this.isExternalAttorney && !this.formModel.isTraineeLawyer;
    },
    isCheckingValidityValid() {
      return (
        (!this.formModel.isTraineeLawyer &&
          Boolean(this.formModel.attorneyNumber) &&
          Boolean(this.lawyerSocialId) &&
          Boolean(this.clientId)) ||
        (Boolean(this.formModel.attorneyNumber) &&
          Boolean(this.lawyerSocialId) &&
          Boolean(this.clientId) &&
          Boolean(this.formModel.mainLawyerSocialId) &&
          Boolean(this.formModel.mainAttorneyNumber))
      );
    },
    isNumberValid() {
      return this.formModel.attorneyNumber !== 0;
    },
    ...mapGetters(['prosecutors', 'defendants']),
    caseParties() {
      return {
        ...this.prosecutors,
        ...this.defendants,
      };
    },
    isVerified() {
      return this.formModel.isVerified;
    },
    clientId() {
      // eslint-disable-next-line eqeqeq
      if (this.formModel.relatedPartyType == partyTypeEnum.organization || (this.formModel.relatedPartyType == partyTypeEnum.person && this.formModel.isOrganizationOwner)) {
        const socialId = (this.caseParties[this.formModel.casePartyObjectKey] || {}).socialId;
        if (socialId) return socialId;
      }
      return this.formModel.commercialRegistrationNumber || this.formModel.socialId;
    },
    isInternalAttorney() {
      // eslint-disable-next-line eqeqeq
      return this.formModel.attorneyTypeId == writingsOfJusticeEnum.internalAttorney;
    },
    isExternalAttorney() {
      // eslint-disable-next-line eqeqeq
      return this.formModel.attorneyTypeId == writingsOfJusticeEnum.externalAttorney;
    },
    isValidTraineeLawyer() {
      return this.isVerified && this.formModel.isTraineeLawyer;
    },
    attorneyData() {
      const attorneyObj = {
        attorneyCode: this.formModel.attorneyNumber,
        agentSocialId: this.lawyerSocialId,
        clientSocialId: this.clientId,
      };
      // tODO: commented for now, we will take a look on it after a while
      // this.formModel.isTraineeLawyer
      // ? {
      //     attorneyCode: this.formModel.attorneyNumber,
      //     agentSocialId: this.lawyerSocialId,
      //     clientSocialId: this.formModel.mainLawyerSocialId,
      //   }
      // : {
      //     attorneyCode: this.formModel.attorneyNumber,
      //     agentSocialId: this.lawyerSocialId,
      //     clientSocialId: this.clientId,
      //   };
      return attorneyObj;
    },
  },
  watch: {
    lawyerSocialId() {
      this.changeVerification(false);
    },
    'formModel.socialId'() {
      this.changeVerification(false);
    },
    'formModel.commercialRegistrationNumber'() {
      this.changeVerification(false);
    },
    'formModel.unifiedNumber'() {
      this.changeVerification(false);
    },
    'formModel.attorneyNumber'() {
      this.isExternalAttorney && this.resetExternalAttorneyDetails();
    },
  },
  mounted() {
    if (this.formModel.representationInfoDetailsChanged) {
      this.resetExternalAttorneyDetails();
    }
    this.$set(
      this.formModel,
      'attorneyTypeId',
      this.formModel.attorneyTypeId || writingsOfJusticeEnum.internalAttorney
    );
  },
  methods: {
    ...mapSysActions(['validateLawyerAttorney', 'validateAgentAttorney']),
    validateAttorneyNumber() {
      this.isChecking = true;
      this.$set(this.formModel, 'isVerified', false);
      if (this.isExternalAttorney && !this.formModel.isTraineeLawyer) {
        this.resetExternalAttorneyDetails();
        this.getExternalAttorneyDetails();
      }
      else {
        const payload = {
          isTraineeLawyer: this.formModel.isTraineeLawyer,
          lawyerSocialId: this.lawyerSocialId,
          attorneyNumber: this.formModel.attorneyNumber,
          mainLawyerSocialId: this.formModel.mainLawyerSocialId,
          mainAttorneyNumber: this.formModel.mainAttorneyNumber,
          clientSocialId: this.clientId,
          attorneyType: this.formModel.attorneyTypeId,
          managerSocialId: this.formModel.socialId,
          commercialRegistrationNumber: this.formModel.commercialRegistrationNumber,
          unifiedNumber: this.formModel.unifiedNumber,
        };
        this.validateLawyerAttorney(payload)
          .then(() => {
            this.toaster.success(this.local.common.verificationSucceeded, 2);
            this.changeVerification(true);
          })
          .finally(() => (this.isChecking = false));
      }
    },
    changeVerification(isVerified) {
      this.$set(this.formModel, 'isVerified', isVerified);
    },
    resetData() {
      this.changeVerification(false);
      if (!this.formModel.isTraineeLawyer) {
        this.$set(this.formModel, 'mainLawyerSocialId', null);
        this.$set(this.formModel, 'mainAttorneyNumber', null);
      }
    },
    showViewAttorneyModal() {
      this.$refs.viewAttorneyModal.show();
    },
    getExternalAttorneyDetails() {
      this.isChecking = true;
      this.validateAgentAttorney({
        attorneyCode: this.formModel.attorneyNumber,
        agentSocialId: this.lawyerSocialId,
        clientSocialId: this.clientId,
        managerSocialId: this.formModel.socialId,
        commercialRegistrationNumber: this.formModel.commercialRegistrationNumber,
        unifiedNumber: this.formModel.unifiedNumber,
        attorneyType: this.formModel.attorneyTypeId,
      })
        .then((result) => {
          if (result) {
            this.$set(this, 'attorneyDetails', result);
            this.$set(this.formModel, 'approvalNumber', result.attorneyNumber);
            this.$set(this.formModel, 'isApprovedFromMofa', result.isApprovedFromMofa);
            this.$set(this.formModel, 'isApprovedFromMoj', true);
            const attorneyCountry = (this.countriesForNonSaudi || []).find(a => a.name == result.kharjiaCountry);
            if (attorneyCountry) {
              this.externalAttorneySourceDisabled = true;
              this.$set(this.formModel, 'externalAttorneySourceId', attorneyCountry.id);
            } else {
              this.externalAttorneySourceDisabled = false;
            }
            this.changeVerification(true);
          }
        })
        .finally(() => {
          this.isChecking = false;
        });
    },
    resetExternalAttorneyDetails() {
      this.$set(this, 'attorneyDetails', null);
      this.$set(this.formModel, 'approvalNumber', null);
      this.$set(this.formModel, 'isApprovedFromMofa', null);
      this.$set(this.formModel, 'isApprovedFromMoj', null);
      this.$set(this.formModel, 'externalAttorneySourceId', null);
      this.changeVerification(false);
    },
    attorneyTypeChanged() {
      this.changeVerification(false);
      this.isExternalAttorney && this.resetExternalAttorneyDetails();
    },
  },
};
</script>
